// ================================================================
// UPDATED NotePopup.js - WITH TRACKING
// Replace the existing NotePopup component
// ================================================================

import React, { useState, useEffect, useRef } from 'react';
import PerformanceBadge from '../PerformanceBadge';
import NoteTypeTag from '../NoteTypeTag';
import AttachmentGallery from './AttachmentGallery';
import RichTextDisplay from './RichTextDisplay';
import FormattedContent from './FormattedContent';
import { notes } from '../api';
import '../styles/noteUrls.css';

function NotePopup({ note, onClose, isNewNote = false, markAsRead }) {
  const [isVisible, setIsVisible] = useState(false);
  const isClosingRef = useRef(false);  // Track if close animation has started

  useEffect(() => {
    // Only show if we haven't started closing
    if (!isClosingRef.current) {
      setTimeout(() => setIsVisible(true), 100);
    }

    // ✅ TRACK VIEW when modal opens
    trackView();
  }, []);

  const trackView = async () => {
    try {
      await notes.trackNoteView(note._id);
    } catch (error) {
      console.error('Error tracking view:', error);
    }
  };

  const handleClose = async () => {
    // Mark that we're closing to prevent re-animation
    isClosingRef.current = true;
    setIsVisible(false);

    // CRITICAL: Schedule onClose AFTER animation completes (500ms), then do async operations
    // This prevents markAsRead from causing parent re-renders during unmounting
    // Extended animation duration (500ms) also masks any brief flickering
    setTimeout(async () => {
      // First: unmount the component from parent
      onClose();

      // Only AFTER unmounting, do the async operations
      // This prevents flickering caused by parent re-renders
      try {
        await notes.trackNoteRead(note._id);
      } catch (error) {
        console.error('Error tracking read:', error);
      }

      // Mark note as read in local state (after component unmounted)
      if (markAsRead && note && note._id) {
        markAsRead(note._id);
      }
    }, 500);
  };

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.6)',
        backdropFilter: 'blur(8px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: '20px',
        opacity: isVisible ? 1 : 0,
        transition: 'opacity 0.5s ease'
      }}
      onClick={handleClose}
    >
      <div
        className="note-popup-card"
        onClick={(e) => e.stopPropagation()}
        style={{
          // NOTE: #f8f9fa here (not #fafbfc) would silently get replaced by
          // darkTheme.css's global override - its rgb() form, rgb(248, 249,
          // 250), starts with the literal substring "rgb(248" which that
          // override's [style*="rgb(248"] selector matches ANYWHERE in the
          // inline style attribute, even inside a gradient stop, blowing
          // away the whole `background` shorthand it's part of. #fafbfc
          // (rgb(250, 251, 252)) is visually identical but doesn't match
          // any of the override's hardcoded rgb-prefix patterns.
          background: 'linear-gradient(135deg, #ffffff 0%, #fafbfc 100%)',
          borderRadius: '24px',
          padding: '32px',
          maxWidth: '500px',
          width: '100%',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
          transform: isVisible ? 'scale(1) translateY(0)' : 'scale(0.9) translateY(20px)',
          transition: 'all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
          position: 'relative'
        }}
      >
        {/* Animated background gradient */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '120px',
          background: 'linear-gradient(135deg, #2f4ba2 0%, #14246b 100%)',
          opacity: 0.1,
          borderRadius: '24px 24px 0 0'
        }} />

        {/* New Note Badge */}
        {isNewNote && (
          <div style={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            color: 'white',
            padding: '6px 16px',
            borderRadius: '20px',
            fontSize: '12px',
            fontWeight: '700',
            boxShadow: '0 4px 12px rgba(245, 87, 108, 0.4)',
            animation: 'pulse 2s infinite'
          }}>
            ✨ NEW
          </div>
        )}

        {/* Content */}
        <div style={{ position: 'relative', zIndex: 1 }}>
          {/* Icon */}
          <div style={{
            width: '80px',
            height: '80px',
            margin: '0 auto 24px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #2f4ba2 0%, #14246b 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '40px',
            boxShadow: '0 8px 24px rgba(47, 75, 162, 0.3)',
            animation: 'badgeBounce 0.8s cubic-bezier(0.68, -0.55, 0.265, 1.55)'
          }}>
            📝
          </div>

          {/* Title */}
          <h2 style={{
            margin: '0 0 8px 0',
            fontSize: '24px',
            fontWeight: '700',
            color: '#2c3e50',
            textAlign: 'center'
          }}>
            {isNewNote ? 'New Feedback!' : 'Note Details'}
          </h2>

          {/* Date */}
          <p style={{
            margin: '0 0 16px 0',
            fontSize: '14px',
            color: '#95a5a6',
            textAlign: 'center'
          }}>
            {new Date(note.createdAt).toLocaleDateString('en-US', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}
          </p>

          {/* Badge and Tag Display */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '24px',
            flexWrap: 'wrap'
          }}>
            {note.noteType && (
              <NoteTypeTag type={note.noteType} size="medium" audience="player" />
            )}

            {note.performanceBadge && (
              <PerformanceBadge level={note.performanceBadge} size="medium" />
            )}
          </div>

          {/* Note text */}
          <div className="note-popup-text" style={{
            background: 'rgba(47, 75, 162, 0.05)',
            padding: '20px',
            borderRadius: '16px',
            border: '2px solid rgba(47, 75, 162, 0.1)',
            marginBottom: '24px'
          }}>
            {note.isPocketNote || note.noteType === 'pocket_recording' ? (
              <RichTextDisplay content={note.text} />
            ) : (
              <div style={{
                fontSize: '16px',
                lineHeight: '1.8',
                color: '#2c3e50'
              }}>
                <FormattedContent content={note.text} />
              </div>
            )}
          </div>

          {/* Photo Attachments - Display photos if they exist */}
          {note.attachments && note.attachments.length > 0 && (
            <div style={{ marginBottom: '24px' }}>
              <AttachmentGallery
                attachments={note.attachments}
                isCoach={false}
                noteId={note._id}
              />
            </div>
          )}

          {/* Action buttons */}
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(note.text);
                  
                  const btn = document.activeElement;
                  const originalText = btn.innerHTML;
                  btn.innerHTML = '✓ Copied!';
                  btn.style.background = 'linear-gradient(135deg, #27ae60 0%, #229954 100%)';
                  
                  setTimeout(() => {
                    btn.innerHTML = originalText;
                    btn.style.background = 'linear-gradient(135deg, #95a5a6 0%, #7f8c8d 100%)';
                  }, 2000);
                } catch (err) {
                  console.error('Failed to copy:', err);
                  alert('Failed to copy text');
                }
              }}
              style={{
                padding: '16px',
                background: 'linear-gradient(135deg, #95a5a6 0%, #7f8c8d 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                fontSize: '16px',
                fontWeight: '600',
                boxShadow: '0 4px 12px rgba(149, 165, 166, 0.3)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                minWidth: '120px'
              }}
            >
              📋 Copy Text
            </button>
            
            <button
              onClick={handleClose}
              style={{
                flex: 1,
                padding: '16px',
                background: 'linear-gradient(135deg, #2f4ba2 0%, #14246b 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                fontSize: '16px',
                fontWeight: '600',
                boxShadow: '0 4px 12px rgba(47, 75, 162, 0.3)'
              }}
            >
              Got it!
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default NotePopup;